import React, { memo, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, Loader, Text } from '@Sage/hoi-poi-ui';

import { CAMPAIGNS_MARKETING, CAMPAIGNS_TEMPLATES } from 'constants/Entities';
import { getLiteral } from 'utils/getLiteral';

import { TEMPLATE_DESIGN_FLOW_ORIGIN } from '../../utils/templateDesignFlow';

import {
    MarketingTemplatesLibraryEmpty,
    MarketingTemplatesSearchEmpty,
} from '../components/MarketingTemplatesEmptyStates';
import PredefinedTemplateCard from '../create/components/PredefinedTemplateCard';
import { useMarketingTemplates } from '../hooks/useMarketingTemplates';
import { useMarketingTemplatesRefresh } from '../MarketingTemplatesRefreshContext';

import '../create/styles.scss';
import './MarketingTemplatesList.scss';

const MarketingTemplatesList = memo(({ footerPreview, onOpenHtmlEditor, onOpenVisualEditor }) => {
    const navigate = useNavigate();
    const searchQuery = useSelector(
        (state) =>
            state.entityFilters?.[CAMPAIGNS_TEMPLATES.entity]?.filters?.matchingName?.value ?? '',
    );

    const { templates, isLoading, error, hasMore, loadMore, refetch } = useMarketingTemplates({
        searchQuery,
    });

    const { refreshNonce } = useMarketingTemplatesRefresh();

    useEffect(() => {
        if (refreshNonce === 0) {
            return;
        }
        refetch();
    }, [refreshNonce, refetch]);

    const handleTemplateLibraryChanged = useCallback(() => {
        refetch();
    }, [refetch]);

    const sentinelRef = useRef(null);

    const handleUseTemplate = useCallback(
        (template) => {
            navigate(`${CAMPAIGNS_MARKETING.route}/new`, {
                state: {
                    campaignTemplateRow: template,
                    designFlowOrigin: TEMPLATE_DESIGN_FLOW_ORIGIN.TEMPLATES_LIST,
                },
            });
        },
        [navigate],
    );

    useEffect(() => {
        const node = sentinelRef.current;
        if (!node || !hasMore || isLoading) {
            return undefined;
        }

        const scrollRoot = node.closest('.marketing-templates__content');

        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (entry?.isIntersecting) {
                    loadMore();
                }
            },
            { root: scrollRoot, rootMargin: '120px', threshold: 0 },
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, [hasMore, isLoading, loadMore, templates.length]);

    const showInitialError = Boolean(error) && !isLoading && templates.length === 0;
    const showSearchEmpty =
        !isLoading && !error && !templates.length && Boolean((searchQuery || '').trim());
    const showLibraryEmpty =
        !isLoading && !error && !templates.length && !(searchQuery || '').trim();

    return (
        <div className="marketing-templates-list">
            <div className="marketing-templates__content marketing-templates-list__inner">
                {isLoading && (
                    <div className="marketing-templates-list__loader" aria-busy="true">
                        <Loader />
                    </div>
                )}

                {showInitialError && (
                    <div className="marketing-templates-list__error">
                        <Text type="body">{getLiteral('error_generalerror')}</Text>
                        <Button type="secondary" onClick={refetch}>
                            {getLiteral('label_retry')}
                        </Button>
                    </div>
                )}

                {showSearchEmpty && <MarketingTemplatesSearchEmpty />}

                {showLibraryEmpty && <MarketingTemplatesLibraryEmpty />}

                {!isLoading && !showInitialError && !showSearchEmpty && !showLibraryEmpty && (
                    <>
                        <div className="marketing-templates-grid">
                            {templates.map((row, index) => (
                                <PredefinedTemplateCard
                                    key={row.id != null ? String(row.id) : `tpl-${index}`}
                                    footerPreview={footerPreview}
                                    showActionsMenu
                                    template={row}
                                    onUseTemplate={handleUseTemplate}
                                    onOpenHtmlEditor={onOpenHtmlEditor}
                                    onOpenVisualEditor={onOpenVisualEditor}
                                    onTemplateLibraryChanged={handleTemplateLibraryChanged}
                                />
                            ))}
                        </div>
                        {hasMore && (
                            <div
                                ref={sentinelRef}
                                className="marketing-templates-list__sentinel"
                                aria-hidden="true"
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
});

MarketingTemplatesList.displayName = 'MarketingTemplatesList';

MarketingTemplatesList.propTypes = {
    footerPreview: PropTypes.shape({
        senderName: PropTypes.string,
        senderAddress: PropTypes.string,
        footerHere: PropTypes.string,
        linkHref: PropTypes.string,
    }),
    onOpenHtmlEditor: PropTypes.func,
    onOpenVisualEditor: PropTypes.func,
};

export default MarketingTemplatesList;
